import * as React from 'react';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Body} from 'shells/typography/body';

import {PaymentInfo} from '../payment-info';

import {BankAccountFields} from './bank-account-fields';
import {CreditCardFields} from './credit-card-fields';
import {CreditDebitSelector} from './credit-debit-selector';
import type {FormChangeInfo, CardValidation, BankValidation, CardForm, BankForm} from './types';

import styles from './billing-form.module.css';

type Props = {
    paymentType: string;
    onUpdateField: (arg1: FormChangeInfo) => void;
    cardForm: CardForm;
    bankForm: BankForm;
    cardValidation: CardValidation | null | undefined;
    bankValidation: BankValidation | null | undefined;
    isElementCardFocused: boolean;
    shouldShowCreditCardForm: boolean;
    prohibitAchBilling: boolean;
    recurlyPaymentErrors: string[];
    cardInfo: any | null | undefined;
    bankInfo: any | null | undefined;
    onUpdate: () => void;
    isEditingPaymentInfo: boolean;
    setIsEditingPaymentInfo: (arg1: boolean) => void;
    isLoadingPaymentInfo: boolean;
    isSavingPaymentInfo: boolean;
    isSaveDisabled: boolean;
};

export function PaymentSection(props: Props) {
    const {
        paymentType,
        onUpdateField,
        cardForm,
        cardValidation,
        bankForm,
        bankValidation,
        prohibitAchBilling,
        shouldShowCreditCardForm,
        isElementCardFocused,
        recurlyPaymentErrors,
        isLoadingPaymentInfo,
        isSavingPaymentInfo,
        isEditingPaymentInfo,
        setIsEditingPaymentInfo,
    } = props;

    const onEditClick = () => {
        setIsEditingPaymentInfo(true);
    };

    const onCancel = () => {
        setIsEditingPaymentInfo(false);
    };

    const onUpdateClick = (e: React.SyntheticEvent<any>) => {
        e.preventDefault();
        props.onUpdate();
    };

    const hasExistingPaymentMethod = Boolean(props.cardInfo || props.bankInfo);
    const shouldShowExistingPaymentInfo = !isEditingPaymentInfo && hasExistingPaymentMethod;

    return (
        <>
            {shouldShowExistingPaymentInfo && (
                <StackLegacy spacing={8}>
                    <PaymentInfo
                        cardInfo={props.cardInfo}
                        bankInfo={props.bankInfo}
                        onEdit={onEditClick}
                        isLoading={isLoadingPaymentInfo}
                    />
                    {recurlyPaymentErrors.length > 0 && (
                        <Body size='normal' color='error'>
                            {recurlyPaymentErrors}
                        </Body>
                    )}
                </StackLegacy>
            )}
            {/* Using 'display: none' here so that Recurly’s Credit Card
element doesn’t get blown away if we were to unmount */}
            <div className={shouldShowExistingPaymentInfo ? styles.hidden : undefined}>
                <StackLegacy spacing={8}>
                    <Body isBold={true}>Payment method</Body>
                    <StackLegacy spacing={16}>
                        {!prohibitAchBilling && (
                            <CreditDebitSelector
                                paymentType={paymentType}
                                onUpdateField={(field, value) => {
                                    onUpdateField({name: 'baseForm', field, value});
                                }}
                            />
                        )}
                        {/* Using 'display: none' here so that Recurly’s Credit Card
element doesn’t get blown away if we were to unmount */}
                        <div
                            className={
                                shouldShowCreditCardForm
                                    ? styles['payment-form-credit-card']
                                    : styles['payment-form-bank']
                            }
                        >
                            <StackLegacy spacing={4}>
                                <CreditCardFields
                                    cardForm={cardForm}
                                    cardValidation={cardValidation}
                                    isElementCardFocused={isElementCardFocused}
                                    onUpdateField={(field, value) => {
                                        onUpdateField({name: 'cardForm', field, value});
                                    }}
                                    requireFields={paymentType === 'card'}
                                />
                                <BankAccountFields
                                    bankForm={bankForm}
                                    bankValidation={bankValidation}
                                    onUpdateField={(field, value) => {
                                        onUpdateField({name: 'bankForm', field, value});
                                    }}
                                    requireFields={paymentType === 'bank'}
                                />
                                {recurlyPaymentErrors.length > 0 && (
                                    <Body size='normal' color='error'>
                                        {recurlyPaymentErrors}
                                    </Body>
                                )}
                            </StackLegacy>
                        </div>
                    </StackLegacy>
                    {/* if we're updating existing payment method, offer a separate set of buttons - if it's the first oayment method, offer a different set of buttons
                    we'll use the main form update button */}
                    {hasExistingPaymentMethod && (
                        <div className='flex gap-12 justify-end'>
                            <Button variant='text-secondary' onClick={onCancel} noPadding={true}>
                                Cancel
                            </Button>
                            <SaveButton
                                errorSaving={recurlyPaymentErrors.length > 0}
                                buttonText='Update payment method'
                                isSaving={isSavingPaymentInfo}
                                // @ts-expect-error
                                onClick={onUpdateClick}
                                resetOnComplete={true}
                                disabled={props.isSaveDisabled}
                                size='small'
                            />
                        </div>
                    )}{' '}
                </StackLegacy>
            </div>
        </>
    );
}
